// ============================================================
// SALT — Match Center Component
// Active matches VS display + incoming direct challenges
// Gold glow for challenges needing attention, red for live
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import type { ActiveMatch, IncomingChallenge, UserTeamInfo } from '../../types/dashboard.types';
import { formatMatchFormat, getTimeRemaining, DefaultTeamIcon } from './DashboardHelpers';
import Button from '../ui/Button';

// -- Props ----------------------------------------------------

interface MatchCenterProps {
  activeMatches: ActiveMatch[];
  incomingChallenges: IncomingChallenge[];
  userTeam: UserTeamInfo | null;
  loading: boolean;
  isAuthenticated: boolean;
  onNavigate: (path: string) => void;
  onAcceptChallenge: (matchId: number) => Promise<void>;
  onDeclineChallenge: (matchId: number) => Promise<void>;
}

// -- Countdown helper -----------------------------------------

function getCountdownText(targetTime: string): string | null {
  const now = Date.now();
  const target = new Date(targetTime).getTime();
  const diff = target - now;

  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);

  if (days > 0) return `Starts in ${days}d ${hours}h`;
  if (hours > 0) return `Starts in ${hours}h ${minutes}m`;
  return `Starts in ${minutes}m`;
}

// -- Countdown hook (ticks every 30s) -------------------------

function useCountdown(targetTime: string | null, isLive: boolean): string | null {
  const [text, setText] = useState<string | null>(() =>
    !isLive && targetTime ? getCountdownText(targetTime) : null
  );

  useEffect(() => {
    if (isLive || !targetTime) {
      setText(null);
      return;
    }

    setText(getCountdownText(targetTime));
    const interval = setInterval(() => {
      setText(getCountdownText(targetTime));
    }, 30000);

    return () => clearInterval(interval);
  }, [targetTime, isLive]);

  return text;
}

// -- Active Match Card ----------------------------------------

const ActiveMatchCard: React.FC<{
  match: ActiveMatch;
  onNavigate: (path: string) => void;
}> = ({ match, onNavigate }) => {
  const { teamA, teamB, matchFormat, matchState, matchUrl, scheduledAt, startedAt } = match;
  const isLive = matchState === 'in_progress' || 
    (matchState === 'accepted' && !!startedAt && new Date(startedAt).getTime() <= Date.now());
  const countdownTarget = scheduledAt || startedAt;
  const countdown = useCountdown(countdownTarget, isLive);

  return (
    <div className={`sl-active-match-card${isLive ? ' sl-active-match-card--live' : ''}`}>
      {/* Badges */}
      <div className="sl-active-match-meta">
        <span className="sl-badge sl-badge--format">
          {formatMatchFormat(matchFormat)}
        </span>
        {isLive ? (
          <span className="sl-badge sl-badge--live">
            <span className="sl-badge-live-dot" />
            Live Now
          </span>
        ) : countdown ? (
          <span className="sl-badge sl-badge--countdown">{countdown}</span>
        ) : (
          <span className="sl-badge sl-badge--state">Ready</span>
        )}
      </div>

      {/* Versus Layout */}
      <div className="sl-active-match-versus">
        {/* Team A */}
        <div className="sl-active-match-team">
          <div className="sl-active-match-team-logo-wrap">
            {teamA.logoUrl ? (
              <img src={teamA.logoUrl} alt="" className="sl-active-match-team-logo" />
            ) : (
              <DefaultTeamIcon />
            )}
          </div>
          <div className="sl-active-match-team-info">
            <span className="sl-active-match-team-name">{teamA.teamName}</span>
            {teamA.rank && (
              <span className="sl-active-match-team-rank">#{teamA.rank}</span>
            )}
          </div>
        </div>

        {/* VS divider */}
        <span className="sl-active-match-vs-label">VS</span>

        {/* Team B */}
        <div className="sl-active-match-team sl-active-match-team--right">
          <div className="sl-active-match-team-info" style={{ textAlign: 'right' }}>
            <span className="sl-active-match-team-name">{teamB.teamName}</span>
            {teamB.rank && (
              <span className="sl-active-match-team-rank">#{teamB.rank}</span>
            )}
          </div>
          <div className="sl-active-match-team-logo-wrap">
            {teamB.logoUrl ? (
              <img src={teamB.logoUrl} alt="" className="sl-active-match-team-logo" />
            ) : (
              <DefaultTeamIcon />
            )}
          </div>
        </div>
      </div>

      {/* Detail button */}
      <button
        className="sl-btn sl-btn-outline sl-active-match-detail-btn"
        onClick={() => onNavigate(matchUrl)}
      >
        View Match Details
      </button>
    </div>
  );
};

// -- Incoming Challenge Card (gold glow) ----------------------

const IncomingChallengeCard: React.FC<{
  challenge: IncomingChallenge;
  userTeam: UserTeamInfo;
  onAccept: (matchId: number) => Promise<void>;
  onDecline: (matchId: number) => Promise<void>;
}> = ({ challenge, userTeam, onAccept, onDecline }) => {
  const { challengingTeam, matchFormat, expiresAt, matchId } = challenge;
  const [actionLoading, setActionLoading] = useState<'accept' | 'decline' | null>(null);

  const handleAccept = useCallback(async () => {
    setActionLoading('accept');
    try {
      await onAccept(matchId);
    } finally {
      setActionLoading(null);
    }
  }, [matchId, onAccept]);

  const handleDecline = useCallback(async () => {
    setActionLoading('decline');
    try {
      await onDecline(matchId);
    } finally {
      setActionLoading(null);
    }
  }, [matchId, onDecline]);

  const busy = actionLoading !== null;

  return (
    <div className="sl-active-match-card sl-active-match-card--challenge">
      {/* Badges */}
      <div className="sl-active-match-meta">
        <span className="sl-badge sl-badge--format">
          {formatMatchFormat(matchFormat)}
        </span>
        <span className="sl-badge sl-badge--challenge">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 3v2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="5" cy="7" r="0.5" fill="currentColor" />
          </svg>
          Direct Challenge
        </span>
        {expiresAt && (
          <span className="sl-badge sl-badge--countdown">
            {getTimeRemaining(expiresAt)}
          </span>
        )}
      </div>

      {/* Versus Layout */}
      <div className="sl-active-match-versus">
        {/* Challenging team (Team A) */}
        <div className="sl-active-match-team">
          <div className="sl-active-match-team-logo-wrap">
            {challengingTeam.logoUrl ? (
              <img src={challengingTeam.logoUrl} alt="" className="sl-active-match-team-logo" />
            ) : (
              <DefaultTeamIcon />
            )}
          </div>
          <div className="sl-active-match-team-info">
            <span className="sl-active-match-team-name">{challengingTeam.teamName}</span>
            {challengingTeam.rank && (
              <span className="sl-active-match-team-rank">#{challengingTeam.rank}</span>
            )}
          </div>
        </div>

        {/* VS divider */}
        <span className="sl-active-match-vs-label">VS</span>

        {/* Your team (Team B — challenged) */}
        <div className="sl-active-match-team sl-active-match-team--right">
          <div className="sl-active-match-team-info" style={{ textAlign: 'right' }}>
            <span className="sl-active-match-team-name">Your Team</span>
          </div>
          <div className="sl-active-match-team-logo-wrap">
            <DefaultTeamIcon />
          </div>
        </div>
      </div>

      {/* Accept / Decline actions */}
      <div className="sl-challenge-actions">
        <button
          className="sl-btn sl-btn-challenge-accept"
          onClick={handleAccept}
          disabled={busy}
        >
          {actionLoading === 'accept' ? (
            <>
              <svg className="sl-btn-spinner" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path d="M7 1.5a5.5 5.5 0 015.16 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Accepting…
            </>
          ) : 'Accept'}
        </button>
        <button
          className="sl-btn sl-btn-challenge-decline"
          onClick={handleDecline}
          disabled={busy}
        >
          {actionLoading === 'decline' ? 'Declining…' : 'Decline'}
        </button>
      </div>
    </div>
  );
};

// -- Component ------------------------------------------------

const MatchCenter: React.FC<MatchCenterProps> = ({
  activeMatches,
  incomingChallenges,
  userTeam,
  loading,
  isAuthenticated,
  onNavigate,
  onAcceptChallenge,
  onDeclineChallenge,
}) => {
  const totalItems = activeMatches.length + incomingChallenges.length;

  // Loading
  if (loading) {
    return (
      <section className="sl-card sl-card--match-center">
        <div className="sl-card-header">
          <span className="sl-card-title">Match Center</span>
        </div>
        <div className="sl-skeleton-row sl-skeleton-pulse" style={{ height: 80 }} />
      </section>
    );
  }

  // Guest
  if (!isAuthenticated) {
    return (
      <section className="sl-card sl-card--match-center">
        <div className="sl-card-header">
          <span className="sl-card-title">Match Center</span>
        </div>
        <h3 className="sl-match-center-heading">Active Matches</h3>
        <div className="sl-match-center-empty">
          <p className="sl-match-center-empty-text">
            Sign in to see your active matches and compete.
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onNavigate('/auth')}
          >
            Sign In
          </Button>
        </div>
      </section>
    );
  }

  // No team
  if (!userTeam) {
    return (
      <section className="sl-card sl-card--match-center">
        <div className="sl-card-header">
          <span className="sl-card-title">Match Center</span>
        </div>
        <h3 className="sl-match-center-heading">No Team Yet</h3>
        <p className="sl-card-subtitle">
          You're not on a team for this season yet. Create a team to start
          competing.
        </p>
        <Button
          variant="primary"
          size="sm"
          style={{ marginTop: '0.6rem' }}
          onClick={() => onNavigate('/create-team')}
        >
          Create a Team
        </Button>
      </section>
    );
  }

  // Has team but nothing to show
  if (totalItems === 0) {
    return (
      <section className="sl-card sl-card--match-center">
        <div className="sl-card-header">
          <span className="sl-card-title">Match Center</span>
        </div>
        <h3 className="sl-match-center-heading">Active Matches</h3>
        <div className="sl-match-center-empty">
          <p className="sl-match-center-empty-text">No active matches right now.</p>
          <p className="sl-card-subtitle">
            Create or accept a match to see live details here.
          </p>
          {userTeam.role === 'captain' && (
            <Button
              variant="primary"
              size="sm"
              style={{ marginTop: '0.6rem' }}
              onClick={() => onNavigate('/matches/create')}
            >
              Create Match
            </Button>
          )}
        </div>
      </section>
    );
  }

  // Active matches + incoming challenges
  const liveCount = activeMatches.filter((m) => m.matchState === 'in_progress').length;

  return (
    <section className="sl-card sl-card--match-center">
      <div className="sl-card-header">
        <span className="sl-card-title">Match Center</span>
        {totalItems > 1 && (
          <span className="sl-match-center-count">
            {totalItems} {totalItems === 1 ? 'match' : 'matches'}
          </span>
        )}
      </div>
      <h3 className="sl-match-center-heading">
        Active Matches
        {liveCount > 0 && (
          <span className="sl-match-center-live-indicator">
            <span className="sl-badge-live-dot" />
            {liveCount} live
          </span>
        )}
        {incomingChallenges.length > 0 && liveCount === 0 && (
          <span className="sl-match-center-challenge-indicator">
            <span className="sl-badge-challenge-dot" />
            {incomingChallenges.length} {incomingChallenges.length === 1 ? 'challenge' : 'challenges'}
          </span>
        )}
      </h3>

      <div className="sl-active-matches-list">
        {/* Incoming challenges first (need attention) */}
        {incomingChallenges.map((challenge) => (
          <IncomingChallengeCard
            key={`challenge-${challenge.matchId}`}
            challenge={challenge}
            userTeam={userTeam}
            onAccept={onAcceptChallenge}
            onDecline={onDeclineChallenge}
          />
        ))}

        {/* Active matches */}
        {activeMatches.map((match) => (
          <ActiveMatchCard
            key={match.matchId}
            match={match}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      {/* Create Match — always visible for captains */}
      {userTeam.role === 'captain' && (
        <Button
          variant="primary"
          size="sm"
          style={{ marginTop: '0.6rem' }}
          onClick={() => onNavigate('/matches/create')}
        >
          Create Match
        </Button>
      )}
    </section>
  );
};

export default MatchCenter;